<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { onUnmounted } from 'vue';
import Sidebarv2Menu from '~/components/sidebarv2/Menu.vue';

defineProps<{
  isRoletasPage: boolean;
}>();

const layoutStore = useLayoutStore();
const { isLayoutMobileOrTablet, topBarHeight, headerHeight } = useLayout();

const { bottomMenu } = storeToRefs(useLayoutStore());

const bottomMenuHeight = computed(() => {
  const height = bottomMenu.value.height;

  return height ? `${height}px` : '0px';
});

const sideBar = ref<HTMLDivElement | null>(null);

const menuEffectFinished = ref(false);
let menuEffectTimeout: ReturnType<typeof setTimeout> | null = null;

const overlayVisible = ref(false);

function onMenuEffectEnd(direction: 'open' | 'close') {
  if (direction === 'open' && layoutStore.menuOpen) {
    menuEffectFinished.value = true;
    if (menuEffectTimeout) {
      clearTimeout(menuEffectTimeout);
      menuEffectTimeout = null;
    }
  }
  layoutStore.setMenuAnimating(false);
}

watch(
  () => layoutStore.menuOpen,
  (isOpen) => {
    layoutStore.setMenuAnimating(true);
    if (isOpen) {
      overlayVisible.value = true;
      menuEffectFinished.value = true;
      if (menuEffectTimeout) clearTimeout(menuEffectTimeout);
      menuEffectTimeout = setTimeout(() => {
        menuEffectFinished.value = true;
        menuEffectTimeout = null;
        layoutStore.setMenuAnimating(false);
      }, 250);
    } else {
      menuEffectFinished.value = false;
      if (menuEffectTimeout) {
        clearTimeout(menuEffectTimeout);
        menuEffectTimeout = null;
      }
      setTimeout(() => {
        overlayVisible.value = false;
        layoutStore.setMenuAnimating(false);
      }, 250);
    }
  },
);

onUnmounted(() => {
  if (menuEffectTimeout) {
    clearTimeout(menuEffectTimeout);
  }
});

const { width: sideBarWidth } = useElementSize(
  sideBar,
  { height: 0, width: 0 },
  { box: 'border-box' },
);

watch(sideBarWidth, (newWidth) => {
  layoutStore.setSideBarWidth(newWidth);
});

const topPositionInPixels = computed<string>(() => {
  if (isLayoutMobileOrTablet.value) {
    return '0px';
  }

  return `${topBarHeight.value + headerHeight.value}px`;
});
</script>

<template>
  <!-- Overlay escuro para mobile -->
  <div
    v-if="isLayoutMobileOrTablet && overlayVisible"
    class="sidebar-overlay"
    @click="layoutStore.closeMenu()"
  />

  <div
    id="divSidebarMenu"
    ref="sideBar"
    class="sidebar no-scrollbar transition"
    :class="[{ open: layoutStore.menuOpen }]"
  >
    <div class="menu-wrapper">
      <Sidebarv2Menu @menu-effect-end="onMenuEffectEnd" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 554;
}

.sidebar {
  @apply fixed overflow-hidden overflow-y-auto whitespace-nowrap left-0 bottom-0 z-[555];
  background-color: #000717;
  width: 320px;
  max-width: 85vw;
  opacity: 0;
  transform: translateX(-100%);
  transition:
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  top: v-bind(topPositionInPixels);
  will-change: transform, opacity;
  @apply lg:w-[70px];
  /* Desktop: sidebar passa a ser inline (parte do fluxo), rola junto
     com a página (scroll único). Não tem scroll próprio. */
  @media screen and (min-width: 1024px) {
    position: relative;
    top: auto;
    bottom: auto;
    left: auto;
    width: 70px;
    max-width: none;
    opacity: 1;
    transform: none;
    transition: none;
    will-change: auto;
    overflow: visible;
    flex-shrink: 0;
    /* z-index baixo para não cobrir o header sticky (z-90) ao rolar. */
    z-index: 1;
    /* Respiro do header conforme Figma: distância de 18px do header
       até os botões Cassino/Esportes. O .sidebarContent já aplica
       pt-[10px], então 8px aqui completa os 18px. */
    padding-top: 8px;
  }
  .menu-wrapper {
    @apply w-full;
    min-width: 0;
    padding-bottom: v-bind(bottomMenuHeight);
    @apply lg:pb-0;
  }
}

.sidebar.open {
  opacity: 1;
  transform: translateX(0);
  @apply lg:z-[560] lg:w-[280px];
  @media screen and (min-width: 1024px) {
    width: 280px;
    /* mantém o comportamento inline definido em .sidebar */
    position: relative;
    /* z-index baixo no desktop para não cobrir o header sticky. */
    z-index: 1;
  }
  .menu-wrapper {
    @apply lg:min-w-[279px];
  }
}
</style>
